import React, { useEffect, useState, useCallback } from "react";
import { displayName } from "@/lib/names";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { datePhrase, lifeLabel } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PersonAvatar } from "@/components/PersonAvatar";
import { OriginsSection } from "@/components/OriginsSection";
import { PersonForm } from "@/components/PersonForm";
import { AudioPlayer } from "@/components/AudioRecorder";
import { SuggestionForm } from "@/components/SuggestionForm";
import { VerificationControls } from "@/components/VerificationControls";
import { VerificationBadge } from "@/components/VerificationBadge";
import { ProvisionalTag } from "@/components/PersonPicker";
import { SourcesSection } from "@/components/SourcesSection";
import { QRDialog } from "@/components/QRDialog";
import { MergeDialog } from "@/components/MergeDialog";
import { HistoryPanel } from "@/components/HistoryPanel";
import { FamilyRelations } from "@/components/FamilyRelations";
import { LINK_TYPES } from "@/lib/relations";
import { ContributionDialog } from "@/components/ContributionDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Edit3, UserPlus, Mic } from "lucide-react";
import { toast } from "sonner";

export const PersonDetailPage = () => {
  const { id } = useParams();
  const { API, user } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [person, setPerson] = useState(null);
  const [clans, setClans] = useState([]);
  const [allPersons, setAllPersons] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const [p, cl, all] = await Promise.all([
      axios.get(`${API}/persons/${id}`),
      axios.get(`${API}/clans`),
      axios.get(`${API}/persons`),
    ]);
    if (p.data.merged_into) { nav(`/persons/${p.data.merged_into}`, { replace: true }); return; }
    setPerson(p.data);
    setClans(cl.data);
    setAllPersons(all.data);
  }, [API, id, nav]);

  useEffect(() => { load(); }, [load]);

  if (!person) return <div className="p-10 text-center text-[#6E5B4F]">{t("loading")}</div>;

  const clan = clans.find(c => c.key === person.clan_key);
  const isLogged = user && user !== false;

  const Rel = ({ label, person: p }) => (
    <div data-testid={`relation-${label}`}>
      <div className="label-uppercase text-[#6E5B4F] mb-2">{label}</div>
      {p ? (
        <Link to={`/persons/${p.id}`} className="flex items-center gap-3 heritage-card p-3 hover:border-[#993D22]">
          <PersonAvatar person={p} size={40} />
          <div>
            <div className="font-serif-heritage text-lg text-[#261911]">{displayName(p)}</div>
            <div className="text-xs text-[#6E5B4F]">{lifeLabel(p)}</div>
          </div>
        </Link>
      ) : (
        <div className="text-sm italic text-[#6E5B4F]">— {t("none")} —</div>
      )}
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10" data-testid="person-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#993D22] flex items-center gap-1 mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" />
      </button>

      <div className="heritage-card p-5 sm:p-8 mb-8 relative">
        <div className="flex flex-col md:flex-row gap-6">
          <PersonAvatar person={person} size={160} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
              <div>
                {clan && (
                  <div className="label-uppercase mb-2" style={{ color: clan.color }}>{clan.name}</div>
                )}
                <div className="flex items-center flex-wrap gap-3">
                  <h1 className="font-serif-heritage text-4xl sm:text-5xl text-[#261911] leading-tight break-words">
                    {displayName(person)}
                  </h1>
                  <VerificationBadge status={person.verification_status || "to_verify"} size="lg" />
                  {person.is_provisional && <span title="Fiche provisoire à compléter"><ProvisionalTag /></span>}
                  {person.is_private && <span className="text-xs px-2 py-0.5 rounded-full bg-[#3A2312] text-[#FAF7F2]" data-testid="private-badge">Fiche privée</span>}
                </div>
                <p className="text-xs text-[#6E5B4F] mt-1" data-testid="record-type">
                  {person.is_provisional ? "Fiche provisoire : informations partielles, à compléter ou à rattacher à une fiche existante." : "Fiche généalogique"} · ce n'est pas un compte utilisateur.
                </p>
                {(person.alt_names || []).length > 0 && (
                  <p className="text-sm text-[#6E5B4F] mt-1" data-testid="alt-names">Autres appellations : {person.alt_names.join(" · ")}</p>
                )}
                {person.nickname && (
                  <p className="italic text-[#6E5B4F] mt-1">« {person.nickname} »</p>
                )}
                <div className="mt-4 flex flex-wrap gap-3 text-sm text-[#3A2312]">
                  {person.birth_year && (
                    <span className="px-3 py-1 rounded-full bg-[#F0E6D8]" data-testid="person-birth-date">né·e {datePhrase(person, "birth")}{person.birth_place ? ` · ${person.birth_place}` : ""}</span>
                  )}
                  {person.death_year && (
                    <span className="px-3 py-1 rounded-full bg-[#F0E6D8]" data-testid="person-death-date">décédé·e {datePhrase(person, "death")}</span>
                  )}
                </div>
              </div>
              {isLogged ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <QRDialog title={t("qrPersonTitle")} label={t("qrPerson")} testId="person-qr-btn" />
                  <ContributionDialog person={person} allPersons={allPersons} clans={clans} />
                  <Button onClick={() => setOpen(true)} variant="outline" className="border-[#993D22] text-[#993D22]" data-testid="edit-person-btn">
                    <Edit3 className="w-4 h-4 mr-2" /> {t("editPerson")}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <QRDialog title={t("qrPersonTitle")} label={t("qrPerson")} testId="person-qr-btn" />
                  <SuggestionForm personId={person.id} />
                </div>
              )}
            </div>
            {person.biography && (
              <div className="mt-6">
                <div className="label-uppercase text-[#6E5B4F] mb-2">{t("oralHistory")}</div>
                <p className="text-[#3A2312] whitespace-pre-wrap leading-relaxed">{person.biography}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <Rel label={`${t("father")}${person.father_link_type && person.father_link_type !== "biological" ? ` (lien ${LINK_TYPES[person.father_link_type]})` : ""}`} person={person.father} />
        <Rel label={`${t("mother")}${person.mother_link_type && person.mother_link_type !== "biological" ? ` (lien ${LINK_TYPES[person.mother_link_type]})` : ""}`} person={person.mother} />
      </div>

      <OriginsSection person={person} clan={clan} />

      <div className="mb-8">
        <VerificationControls person={person} onChanged={load} />
      </div>

      {person.is_provisional && isLogged && (
        <div className="mb-8"><MergeDialog person={person} allPersons={allPersons} /></div>
      )}

      <SourcesSection person={person} canEdit={isLogged} onChanged={load} />

      <FamilyRelations person={person} allPersons={allPersons} />

      {person.audio_recordings && person.audio_recordings.length > 0 && (
        <div className="mb-8" data-testid="audio-section">
          <div className="label-uppercase text-[#6E5B4F] mb-3 flex items-center gap-2">
            <Mic className="w-3.5 h-3.5" /> {t("audioRecordings")}
          </div>
          <ul className="space-y-2">
            {person.audio_recordings.map((a, idx) => (
              <li key={a.path} className="heritage-card p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`audio-play-${idx}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-heritage text-lg text-[#261911]">{a.title || `Récit ${idx + 1}`}</div>
                  {a.duration_seconds ? (
                    <div className="label-uppercase text-[#6E5B4F] mt-1">{Math.round(a.duration_seconds)}s</div>
                  ) : null}
                </div>
                <AudioPlayer path={a.path} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {isLogged && <HistoryPanel personId={person.id} refreshKey={person.updated_at} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">{t("editPerson")}</DialogTitle>
          </DialogHeader>
          <PersonForm
            initial={person}
            clans={clans}
            allPersons={allPersons}
            onDone={() => { setOpen(false); load(); }}
            onCancel={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonDetailPage;
